import { useState, useMemo } from 'react';
import { createRoute } from '@tanstack/react-router';
import { Handshake } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { OfferCard } from '../components/community/OfferCard';
import { useOffers } from '../hooks/useCommunity';
import { useVault } from '../context/VaultContext';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './offers.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offers',
  component: OffersPage,
});

function OffersPage() {
  const { t } = useTranslation();
  const { multiVaultEnabled, vaults } = useVault();
  const [direction, setDirection] = useState<'all' | 'sent' | 'received'>('all');
  const [status, setStatus] = useState<'all' | 'pending'>('all');
  const [vaultFilter, setVaultFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const query: Record<string, string> = {
    direction,
    status,
    page: String(page),
    limit: '50',
  };

  const { data, isLoading } = useOffers(query);
  const allOffers = data?.data ?? [];

  // Client-side vault filtering — offers are user-scoped, filter by item vault membership
  const offers = useMemo(() => {
    if (vaultFilter === 'all') return allOffers;
    return allOffers.filter((offer) =>
      offer.items.some((item) => item.vaultName === vaultFilter)
    );
  }, [allOffers, vaultFilter]);

  const tabs = [
    { key: 'all', label: t('community.allOffers') },
    { key: 'received', label: t('community.received') },
    { key: 'sent', label: t('community.sent') },
  ] as const;

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('community.offers')}</h1>
        </div>

        {/* Direction tabs */}
        <div className={styles.tabBar}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${direction === tab.key ? styles.tabActive : ''}`}
              onClick={() => { setDirection(tab.key); setPage(1); }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters row: status + vault */}
        <div className={styles.filtersRow}>
          <div className={styles.statusFilters}>
            <button
              className={`${styles.statusChip} ${status === 'all' ? styles.statusChipActive : ''}`}
              onClick={() => { setStatus('all'); setPage(1); }}
            >
              {t('community.allStatuses')}
            </button>
            <button
              className={`${styles.statusChip} ${status === 'pending' ? styles.statusChipActive : ''}`}
              onClick={() => { setStatus('pending'); setPage(1); }}
            >
              {t('community.pendingOnly')}
            </button>
          </div>

          {multiVaultEnabled && vaults.length > 0 && (
            <div className={styles.vaultFilters}>
              <button
                className={`${styles.statusChip} ${vaultFilter === 'all' ? styles.statusChipActive : ''}`}
                onClick={() => setVaultFilter('all')}
              >
                All Vaults
              </button>
              {vaults.map((vault) => (
                <button
                  key={vault.id}
                  className={`${styles.statusChip} ${vaultFilter === vault.name ? styles.statusChipActive : ''}`}
                  onClick={() => setVaultFilter(vault.name)}
                >
                  {vault.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div>{t('community.loadingOffers')}</div>
        ) : offers.length === 0 ? (
          <div className={styles.emptyState}>
            <Handshake size={56} strokeWidth={1} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>{t('community.noOffers')}</div>
            <div className={styles.emptyDesc}>{t('community.noOffersDesc')}</div>
          </div>
        ) : (
          <div className={styles.grid}>
            {offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              &larr;
            </button>
            <span className={styles.pageInfo}>{page} / {data.totalPages}</span>
            <button
              className={styles.pageBtn}
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              &rarr;
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}
